<template>
  <div ref="editor_container" class="editor-container">
    <div class="editor-menu" ref="editor_container_element" :class="{active: editor_menu_visible}">
        <div class="menu">
          <div ref="editor_menu_actions" class="actions">
            <DropdownList id="action-font-family" class="font-family hide-tablet">
                <template #select>
                  <ActionButton
                      v-tooltip:top='"editor.typeface"'
                      class='outline'
                      :text="editor.state.font"
                      />
                </template>
                <template #options>
                  <ActionButton v-for="font in editor.options.getFontNames()" :key="font"
                      @click="onSetFont(font)"
                      :class="{active: editor.state.font === font}"
                      :text="font"
                      />
                </template>
            </DropdownList>

            <DropdownList id="action-font-size" class="font-size hide-tablet">
                <template #select>
                  <ActionButton
                      v-tooltip:top='"editor.font_size"'
                      icon='fas fa-text-height'
                      class='outline'
                      />
                </template>
                <template #options>
                  <ActionButton
                      v-for="size in editor.options.sizes" :key="size"
                      @click="onSetSize(size)"
                      :class="{active: editor.state.size === size}"
                      :text="'editor.sizes.' + size"
                      />
                </template>
            </DropdownList>

            <span id="action-font-type" class="button-group ">
                <ActionButton
                  @click="onSetBold()"
                  v-tooltip:top='"editor.bold"'
                  class='outline'
                  :class="{active : editor.state.bold}"
                  icon='fas fa-bold'
                  />
                <ActionButton
                  @click="onSetItalic()"
                  v-tooltip:top='"editor.italic"'
                  class='outline'
                  :class="{active : editor.state.italic}"
                  icon='fas fa-italic'
                  />
                <ActionButton
                  @click="onSetUnderline()"
                  v-tooltip:top='"editor.underline"'
                  class='outline'
                  :class="{active : editor.state.underline}"
                  icon='fas fa-underline'
                  />
                <ActionButton
                  @click="onSetStrike()"
                  v-tooltip:top='"editor.strike"'
                  class='outline'
                  :class="{active : editor.state.strike}"
                  icon='fas fa-strikethrough'
                  />
            </span>

            <DropdownList id="action-font-color" class="font-color hide-tablet">
                <template #select>
                  <ActionButton
                      v-tooltip:top='"editor.color"'
                      icon='fas fa-tint '
                      class='outline'
                      :style="{'color':editor.state.color,'background-color':editor.state.background_color}"
                      />
                </template>
                <template #options>
                  <ActionButton
                      :class='{active : color_tab_selected === "color"}'
                      @click.prevent.stop="onSetColorTab('color')"
                      text="editor.letter_color"
                      />
                  <ActionButton
                      :class='{active : color_tab_selected === "background"}'
                      @click.prevent.stop="onSetColorTab('background')"
                      text="editor.background_color"
                      />
                  <div class="tab" :class='{active : color_tab_selected === "color"}'>
                      <ActionButton
                        v-for="color in editor.options.colors" :key="color"
                        icon='fas fa-square-full'
                        class='inline'
                        :class="{active : editor.state.color === color}"
                        :style="'color:' + color"
                        @click="onSetColor(color)"
                        />
                  </div>
                  <div class="tab" :class='{active : color_tab_selected === "background"}'>
                      <ActionButton
                        v-for="color in editor.options.colors" :key="color"
                        icon='fas fa-square-full'
                        class='inline'
                        :class="{active : editor.state.background_color === color}"
                        :style="'color:' + color"
                        @click="onSetBackgroundColor(color)"
                        />
                  </div>
                </template>
            </DropdownList>

            <DropdownList id="action-lists" class="lists hide-tablet">
                <template #select>
                  <ActionButton
                      v-tooltip:top='"editor.lists_quotes"'
                      :icon='icons[getActiveListIcon()]'
                      class='outline'
                      />
                </template>
                <template #options>
                  <ActionButton
                      v-tooltip:top='"editor.ordered_list"'
                      :class="{active : editor.state.ordered_list}"
                      :icon='icons["ordered_list"]'
                      @click="onSetOrderedList()"
                      />
                  <ActionButton
                      v-tooltip:top='"editor.unordered_list"'
                      :icon='icons["unordered_list"]'
                      :class="{active : editor.state.unordered_list}"
                      @click="onSetUnorderedList()"
                      />
                  <ActionButton
                      v-tooltip:top='"editor.quote"'
                      :icon='icons["quote"]'
                      :class="{active : editor.state.quote}"
                      @click="onSetQuote()"
                      />
                </template>
            </DropdownList>

            <DropdownList  id="action-alignment" class="alignment hide-tablet">
                <template #select>
                  <ActionButton
                      v-tooltip:top='"editor.text_align"'
                      :icon='icons[editor.state.alignment] || icons["align-left"]'
                      class='outline'
                      />
                </template>
                <template #options>
                  <ActionButton
                      v-tooltip:top='"editor.align_left"'
                      :icon='icons["align-left"]'
                      @click="onSetAlignment('align-left')"
                      />
                  <ActionButton
                      v-tooltip:top='"editor.align_center"'
                      :icon='icons["align-center"]'
                      @click="onSetAlignment('align-center')"
                      />
                  <ActionButton
                      v-tooltip:top='"editor.align_right"'
                      :icon='icons["align-right"]'
                      @click="onSetAlignment('align-right')"
                      />
                  <ActionButton
                      v-tooltip:top='"editor.justify"'
                      :icon='icons["align-justify"]'
                      @click="onSetAlignment('align-justify')"
                      />
                </template>
            </DropdownList>

            <span id="action-alignment" class="button-group alignment show-tablet hide-mobile-xl">
                <ActionButton
                  v-tooltip:top='"editor.align_left"'
                  :icon='icons["align-left"]'
                  class='outline'
                  @click="onSetAlignment('align-left')"
                  />
                  <ActionButton
                  v-tooltip:top='"editor.align_center"'
                  :icon='icons["align-center"]'
                  class='outline'
                  @click="onSetAlignment('align-center')"
                  />
                  <ActionButton
                  v-tooltip:top='"editor.align_right"'
                  :icon='icons["align-right"]'
                  class='outline'
                  @click="onSetAlignment('align-right')"
                  />
                  <ActionButton
                  v-tooltip:top='"editor.justify"'
                  :icon='icons["align-justify"]'
                  class='outline'
                  @click="onSetAlignment('align-justify')"
                  />
            </span>

            <span id="action-adds" class="button-group adds add-item">
                <ActionButton
                    v-tooltip:top='"editor.link"'
                    class='outline'
                    icon='fas fa-link'
                    @click="onLinksModalOpen()"
                    />
                <ActionButton
                    v-tooltip:top='"editor.photo"'
                    class='outline'
                    icon='fas fa-image'
                    @click="onImagesModalOpen()"
                    />
                <ActionButton
                    v-tooltip:top='"editor.emojis"'
                    class='outline'
                    icon='far fa-smile'
                    @click="onEmojiModalOpen()"
                    />
            </span>

            <span id="action-undo" class="button-group action-undo hide-tablet">
                <ActionButton
                  @click="onSetUndo()"
                  v-tooltip:top='"editor.undo"'
                  class='outline'
                  icon='fas fa-undo'
                  />
                <ActionButton
                  @click="onSetRedo()"
                  v-tooltip:top='"editor.redo"'
                  class='outline'
                  icon='fas fa-redo'
                  />
                <ActionButton
                  @click="onClearFormatting()"
                  v-tooltip:top='"editor.remove_formatting"'
                  class='outline'
                  icon='fas fa-eraser'
                  />
            </span>

            <span id="action-undo" class="button-group action-undo show-tablet hide-mobile-xl">
              <ActionButton
                  @click="onClearFormatting()"
                  v-tooltip:top='"editor.remove_formatting"'
                  class='outline'
                  icon='fas fa-eraser'
                  />
            </span>

          </div>
        </div>

        <ActionButton v-if="editor_menu_visible"
            @click="onHideEditorMenu()"
            icon='fas fa-times'
            class='outline'
        />
        <ActionButton v-else
            @click="onShowEditorMenu()"
            icon='fas fa-font'
            class='outline'
        />

    </div>

    <div ref="editor_html_element" id="compose-editor" class="editor-body" autocorrect="off" data-gramm="false">
    </div>

    <div v-show="resizer_image_is_active" :ref="refs_resizer.container" class="resizer-container">
      <div :ref="refs_resizer.top_left" class="resizer-image top-left resizer-active"></div>
      <div :ref="refs_resizer.top_right" class="resizer-image top-right resizer-active"></div>
      <div :ref="refs_resizer.bottom_left" class="resizer-image bottom-left resizer-active"></div>
      <div :ref="refs_resizer.bottom_right" class="resizer-image bottom-right resizer-active"></div>
    </div>

    <div v-if="image_overlay_visible || link_overlay_visible"  :style="link_overlay_style" class="inline-menu">
      <div v-if="image_overlay_visible">
        <span @click="onResizerImageOriginal">Tamanho original</span>
        <span @click="onResizerImagePercent(30)">Pequeno</span>
        <span @click="onResizerImagePercent(80)">Médio</span>
        <span @click="onResizerImagePercent(150)">Grande</span>
        <span @click="onResizerImageRemove">Remover imagem</span>
        <span v-if="!link_overlay_visible" @click="onLinksModalOpen">Adicionar Link</span>
      </div>
      <div v-if="link_overlay_visible">
        <span @click="onNavigateToLink"><span>{{ link_overlay_selected.href }}</span></span>
        <span @click="onOpenLinkChangeModal">Alterar</span>
        <span @click="onRemoveLinkFromEditor">Remover</span>
      </div>
    </div>

  </div>

  <InlineUploadDrop
    @inlineImagesDrop="onInlineImagesDrop"
    @inlineAttachmentsDrop="onInlineAttachmentsDrop"
    :inline_upload_type="inline_upload_type"
  />

  <ImageUploadModal v-if="image_modal_visible"
    @modalWindowClose='onImagesModalClose()'
    @inlineImagesDrop="onInlineImagesDrop"
    @remoteImageSubmit="onRemoteImageSubmit"
  />

  <EmojisEditorModal v-if="emoji_modal_visible"
    :html_editor="editor"
    @modalWindowClose='onEmojiModalClose()'
  />

  <LinksEditorModal v-if="links_modal_visible"
    :html_editor="editor"
    :click_event="last_click_event"
    @modalWindowClose='onLinksModalClose()'
  />
</template>

<script setup>
  import {ref, reactive}                          from 'vue';
  import {defineProps, defineExpose, defineEmits} from 'vue';
  import {onMounted, onUnmounted}                 from 'vue';
  import EmojisEditorModal                        from './Modals/EmojisEditorModal.vue';
  import ImageUploadModal                         from './Modals/ImageUploadModal.vue';
  import LinksEditorModal                         from './Modals/LinksEditorModal.vue';
  import ActionButton                             from '@/vue/Elements/Buttons/ActionButton.vue';
  import DropdownList                             from '@/vue/Elements/DropDowns/DropdownList.vue';
  import InlineUploadDrop                         from '@/vue/Elements/Tools/HTMLEditor/Partials/InlineUploadDrop.vue';
  import NotificationsController                  from '@/js/Webmail/Notifications/NotificationsController';
  import DomController                            from '@/js/Webmail/DomController';
  import HTMLSquireEditor                         from '@/js/Shared/Editor/HTMLSquireEditor';
  import FilesEventManager                        from '@/js/Shared/Events/Managers/FilesEventManager';


  const props = defineProps({
    text_formatting_open: {
      default : true,
    },

    image_size_limit : {
      required: true
    },

    inline_upload_type : {
      default : 'dual'
    },
  })

  const emit = defineEmits([
    'inlineImagesDrop', 'inlineAttachmentsDrop', 'modalOpen', 'contentModified'
  ])

  const editor                    = reactive(new HTMLSquireEditor());
  const editor_html_element       = ref();
  const editor_container_element  = ref();
  const editor_container          = ref();
  const editor_menu_visible       = ref();
  const links_modal_visible       = ref(false);
  const image_modal_visible       = ref(false);
  const emoji_modal_visible       = ref(false);
  const color_tab_selected        = ref('color');
  const link_overlay_visible      = ref(false);
  const link_overlay_style        = ref({});
  const link_overlay_selected     = ref(null);
  const resizer_image_selected    = ref();
  const resizer_image_dimensions  = ref();
  const resizer_image_is_active   = ref(false);
  const image_overlay_visible     = ref(false);
  const image_overlay_style       = ref(null);
  const visual_viewport_changed   = ref(false);
  const last_click_event          = ref({});
  const editor_menu_actions = ref();
  const refs_resizer = {
    'container': ref(),
    'top_left' : ref(),
    'top_right': ref(),
    'bottom_left' : ref(),
    'bottom_right': ref(),
  }

  const icons = {
    'ordered_list'   : 'fas fa-list-ol',
    'unordered_list' : 'fas fa-list-ul',
    'quote'          : 'fas fa-quote-right',
    'code'           : 'fas fa-code',
    'align-left'     : 'fas fa-align-left',
    'align-right'    : 'fas fa-align-right',
    'align-center'   : 'fas fa-align-center',
    'align-justify'  : 'fas fa-align-justify',
  }

  editor.options.fonts = {
    "Arial"                : ["Arial", "Helvetica", "sans-serif"],
    "Comic Sans MS"        : ['"Comic Sans MS"', "cursive"],
    "Courier New"          : ['"Courier New"', "Courier", "monospace"],
    "Georgia"              : ["Georgia", "serif"],
    "Lucida Sans Unicode"  : ['"Lucida Sans Unicode"', '"Lucida Grande"', "sans-serif"],
    "Tahoma"               : ["Tahoma", "Geneva", "sans-serif"],
    "Times New Roman"      : ['"Times New Roman"', "Times", "serif"],
    "Helvetica"            : ["helvetica", "arial"],
    "Trebuchet MS"         : ['"Trebuchet MS"', "Helvetica", "sans-serif"],
    "Verdana"              : ["Verdana", "sans-serif"]
  };

  editor.options.sizes = [
    'small',
    'medium',
    'large',
    'x-large',
  ];

  editor.options.styles = [
    'bold',
    'italic',
    'underline',
    'strike',
  ]

  editor.options.colors = [
    // Line 1
    'rgb(69, 141, 40)',
    'rgb(0, 76, 143)',
    'rgb(85, 3, 140)',
    'rgb(124, 0, 5)',
    'rgb(233, 60, 0)',
    'rgb(255, 217, 18)',
    'rgb(0, 0, 0)',

    // Line 2
    'rgb(86, 178, 51)',
    'rgb(0, 104, 195)',
    'rgb(116, 4, 191)',
    'rgb(179, 0, 8)',
    'rgb(255, 66, 1)',
    'rgb(255, 220, 36)',
    'rgb(51, 51, 51)',

    // Line 3
    'rgb(109, 203, 73)',
    'rgb(0, 132, 248)',
    'rgb(147, 5, 243)',
    'rgb(234, 0, 10)',
    'rgb(255, 84, 24)',
    'rgb(255, 223, 55)',
    'rgb(102, 102, 102)',

    // Line 4
    'rgb(138, 213, 109)',
    'rgb(45, 157, 255)',
    'rgb(170, 50, 250)',
    'rgb(255, 34, 44)',
    'rgb(255, 101, 47)',
    'rgb(255, 226, 73)',
    'rgb(153, 153, 153)',

    // Line 5
    'rgb(168, 224, 146)',
    'rgb(98, 182, 255)',
    'rgb(191, 101, 252)',
    'rgb(255, 90, 97)',
    'rgb(255, 118, 70)',
    'rgb(255, 229, 91)',
    'rgb(204, 204, 204)',

    // Line 6
    'rgb(197, 234, 182)',
    'rgb(150, 206, 255)',
    'rgb(212, 153, 253)',
    'rgb(255, 145, 150)',
    'rgb(255, 135, 93)',
    'rgb(255, 232, 109)',
    'rgb(255, 255, 255)',
  ];

  editor.options.alignments = {
    'align-center' : 'center',
    'align-right'  : 'right',
    'align-left'   : 'left',
    'align-justify': 'justify',
  }

  editor.defaults.font  = 'Arial';
  editor.defaults.size  = 'normal';

  onMounted(() => {
    editor.set(editor_html_element.value, {
      blockTag       : 'p',
      blockAttributes: {}
    });

    editor.squire.addEventListener("pathChange", onEditorPathChange);
    editor.squire.addEventListener("blur"      , onEditorBlur);
    editor.squire.addEventListener('paste'     , onEditorPaste);
    editor.squire.addEventListener('click'     , onEditorClick);
    editor.squire.addEventListener('keyup'     , onEditorKeyUp);
    editor.squire.addEventListener('input'     , onEditorInput);
    window.addEventListener("click", onWindowClick);
    editor.setDefaults();
    setEventCornerResizer();
  });

  onUnmounted(() => {
    window.removeEventListener("click", onWindowClick);
  })

  const setEventCornerResizer = () => {
    manageResizeCornerRefs((resizer_corner_ref, resizer_corner_position) => {
      resizer_corner_ref.value.addEventListener('mousedown', event => onResizeMarkMouseDown(event, resizer_corner_position));
    });
  }

  const onSetFont = (font) => {
    editor.setFontFace(font);
  }

  const onSetSize = (size) => {
    editor.setFontSize(size);
  }

  const onSetBold = () => {
    editor.toggleBold();
  }

  const onSetItalic = () => {
    editor.toggleItalic();
  }

  const onSetUnderline = () => {
    editor.toggleUnderline();
  }

  const onSetStrike = () => {
    editor.toggleStrikethrough();
  }

  const onSetColor = (color) => {
    editor.setColor(color);
  }

  const onSetBackgroundColor = (color) => {
    editor.setBackgroundColor(color);
  }

   const onSetOrderedList = () => {
    editor.toggleOrderedList()
  }

  const onSetUnorderedList = () => {
    editor.toggleUnorderedList()
  }

  const onSetQuote = () => {
    (editor.state.quote) ? editor.setDecreasedQuoteLevel() : editor.setIncreasedQuoteLevel();
  }

  const onSetColorTab = (tab) => {
    setColorTabSelected(tab);
  }

  const onClearFormatting = () => {
    editor.clearFormatting();
  }

  const onSetAlignment = (alignment) => {
    editor.setTextAlignment(alignment)
  }

  const onSetUndo = () => {
    editor.setUndo();
  }

  const onSetRedo = () => {
    editor.setRedo();
  }

  const onInlineImagesDrop = (data) => {
    addImagesToEditor(data);
  }

  const onRemoteImageSubmit = (url) => {
    let img = document.createElement('img');
    img.setAttribute('alt'   , url);
    img.setAttribute('src'   , url);
    editor.insertHTML(img);
  }

  const onInlineAttachmentsDrop = (data) => {
    emit('inlineAttachmentsDrop',
      FilesEventManager.createFilesEvent(data.files)
    );
  }

  const onLinksModalOpen = (event) => {
    setLinksModalVisibility(true);
  }

  const onLinksModalClose = () => {
    setLinksModalVisibility(false);
  }

  const onImagesModalOpen = () => {
    setImagesModalVisibility(true);
  }

  const onImagesModalClose = () => {
    setImagesModalVisibility(false);
  }

  const onEmojiModalClose = () => {
    setEmojiModalVisibility(false);
  }

  const onEmojiModalOpen = () => {
    setEmojiModalVisibility(true);
  }

  const onEditorPathChange = () => {
    editor.monitorStateChanges();
  }

  const onEditorBlur = (event) => {

    if(!editor_container_element.value){
      return;
    }

    if (editor_container_element.value.contains(event.relatedTarget)) {
      editor.squire.focus();
    }

    if (visual_viewport_changed.value === true) {
      setEditorContainerPosition(0);
    }
  }

  const onEditorPaste = (event) => {
    addImagesToEditor(event.clipboardData);
    emitContentModified();
  }

  const onShowEditorMenu = () => {
    setEditorMenuVisibility(true);
  }

  const onHideEditorMenu = () => {
    setEditorMenuVisibility(false);
  }

  const addImagesToEditor = (data) => {
    let images = FilesEventManager.createImagesFilesEvent(data.files);

    if (images.size >= props.image_size_limit) {
      NotificationsController.addError('notifications.editor_image_size_limit', [(props.image_size_limit / 1000)]);
      return;
    }

    editor.insertImages(images);
    emit('inlineImagesDrop', images);
  }

  const setLinksModalVisibility = (value) => {
    links_modal_visible.value = value;
    emit('modalOpen', value);
  }

  const setEmojiModalVisibility = (value) => {
    emoji_modal_visible.value = value;
    emit('modalOpen', value);
  }

  const setImagesModalVisibility = (value, ) => {
    image_modal_visible.value = value;
    emit('modalOpen', value);
  }

  const setLastClickEvent = (event) => {
    last_click_event.value = event;
  }

  const getContents = () => {
    return editor.getContents();
  }

  const setContents = (contents) => {
    editor.setContents(contents);
  }

  const insertHTML = (contents) => {
    editor.insertHTML(contents);
  }

  const removeElement = (id) => {
    editor.removeElement(id);
  }

  const emitContentModified = () => {
    setTimeout(() => {
      emit('contentModified');
    }, 20);
  }

  const getActiveListIcon = () => {
    if (editor.state.ordered_list) {
      return 'ordered_list';
    }

    if (editor.state.unordered_list) {
      return 'unordered_list';
    }

    if (editor.state.quote) {
      return 'quote';
    }

    if (editor.state.code) {
      return 'code';
    }

    return 'ordered_list';
  }

  const setColorTabSelected = (tab) => {
    color_tab_selected.value = tab;
  }

  const onWindowClick = () => {
    onResetEditorClick();
  }

  const onResetEditorClick = () => {
    if(!resizer_image_selected.value){
      return;
    }

    resetResizeOverlayImage();
  }

  const onEditorClick = (event) => {
    event.stopPropagation();

    setLastClickEvent(event);
    onResizerOverlayImage(event);
    handleClickLinkOverlay(event);
  };

  const onResizerOverlayImage = (event) => {
    const target = event.target;

    resetResizeOverlayImage();

    if (target.tagName.toLowerCase() !== 'img') {
      return;
    }

    setResizerImageSelected(target);
    setResizerDivIsActive(true);
    resizerPositionDivImage();
    setImageOverlayVisible(true);
    setPositionOverlayImage();
    setFocusEditorAfterImage();
  }

  const setFocusEditorAfterImage = () => {
    editor.setFocusAfterElement(resizer_image_selected.value)
  }

  const resizerPositionDivImage = () => {
    const size_image = resizer_image_selected.value.getBoundingClientRect();
    const parent_position = editor_container.value.getBoundingClientRect();

    refs_resizer.container.value.style.width = (size_image.width + 2) + 'px';
    refs_resizer.container.value.style.height = (size_image.height + 2) + 'px';
    refs_resizer.container.value.style.top = (size_image.top - parent_position.top) + 'px';
    refs_resizer.container.value.style.left = (size_image.left - parent_position.left) + 'px';
  }

  const setResizerDivIsActive = (value) => {
    resizer_image_is_active.value = value;
  }

  const manageResizeCornerRefs = (callback) => {
    for (const key in refs_resizer) {
      if(key === 'container') continue;

      const resizer_div = refs_resizer[key];
      const position_corner = key.replace('_', '-');

      callback(resizer_div, position_corner);
    }
  };

  const onResizeImage  = (prev_x, prev_y, position, event) => {
    resizeImageHandle(prev_x, prev_y, position, event);
    resizerPositionDivImage();
    setPositionOverlayImage();
  }

  const setPositionOverlayImage = () => {
    const parent_position = editor_container.value.getBoundingClientRect();
    const position_target = resizer_image_selected.value.getBoundingClientRect();

    link_overlay_style.value = {
      left: (position_target.left - parent_position.left) + 'px',
      top:  (position_target.bottom - parent_position.top + 10) + 'px'
    };
  }

  const onResizeMarkMouseDown = (event, position) => {
    event.preventDefault();
    document.addEventListener('mousemove', onResizeMarkMouseMove);
    document.addEventListener('mouseup', onResizeMarkMoveUp);

    editor_html_element.value.classList.add('cursor');
    editor_html_element.value.classList.add(position);

    let prev_x = event.clientX;
    let prev_y = event.clientY;

    function onResizeMarkMouseMove(event) {
      onResizeImage(prev_x, prev_y, position, event);
      prev_x = event.clientX;
      prev_y = event.clientY;
    }

    function onResizeMarkMoveUp(){
      document.removeEventListener('mousemove', onResizeMarkMouseMove);
      document.removeEventListener('mouseup', onResizeMarkMoveUp);
      DomController.removeBodyClass('cursor');
      DomController.removeBodyClass(position);
      resetResizeOverlayImage();
    }
  }

  const resetResizeOverlayImage = () => {
    setResizerDivIsActive(false);
    removeResizerImageSelected();
    setImageOverlayVisible(false);
  }

  const onResizerImageOriginal = () => {
    resizer_image_selected.value.style = {};
    resetResizeOverlayImage();
  };

  const onResizerImagePercent = (percent_width) => {
    const original_width = resizer_image_selected.value.naturalWidth;
    const new_width = original_width * (percent_width / 100);

    resizer_image_selected.value.style.width = new_width + 'px';
    resizer_image_selected.value.style.height = 'auto';
    resetResizeOverlayImage();
  };

  const onInputResizerOverlayImage = (event) => {
    if(!resizer_image_selected.value){
      return;
    }

    resetResizeOverlayImage();
  }

  const onEditorKeyUp = (event) => {
    handleKeyUpLinkOverlay();
    onInputResizerOverlayImage(event);

    if (event.key === 'Backspace' || event.key === 'Delete') {
      emitContentModified();
    }
  };

  const onEditorInput = (event) => {
    handleInputLinkOverlay();
    handleClickLinkOverlay(event);
  }

  const handleInputLinkOverlay = () => {
    if(!link_overlay_selected.value){
      return;
    }

    if (!editor.squire.hasFormat('a', { href: link_overlay_selected.value.href })) {
      resetLinkOverlay()
      return;
    }
  }

  const onResizerImageRemove = () => {
    if(!resizer_image_selected.value){
      return;
    }

    resizer_image_selected.value.parentNode.removeChild(resizer_image_selected.value);
    resetResizeOverlayImage();
  };

  const removeResizerImageSelected = () => {
    if(!resizer_image_selected.value){
      return;
    }

    resizer_image_selected.value = null;
  }

  const setResizerImageSelected = (target) => {
    resizer_image_selected.value = target;
  }

  const resizeImageHandle = (prev_x, prev_y, position, event) => {
    const rect = resizer_image_selected.value.getBoundingClientRect();
    const aspect_ratio = rect.width / rect.height;

    // Use -1 for top, 1 for bottom
    const direction = position.includes('top') ? -1 : 1;

    if (position.includes('bottom') || position.includes('top')) {
      const new_height = rect.height + direction * (event.clientY - prev_y);
      const new_width = new_height * aspect_ratio;
      resizer_image_selected.value.style.height = new_height + 'px';
      resizer_image_selected.value.style.width = new_width + 'px';

      return;
    }

    const new_width = rect.width + (event.clientX - prev_x);
    const new_height = new_width / aspect_ratio;
    resizer_image_selected.value.style.width = new_width + 'px';
    resizer_image_selected.value.style.height = new_height + 'px';
  };

  const handleKeyUpLinkOverlay = () => {
    if (!link_overlay_visible.value || !link_overlay_selected.value) {
      return;
    }

    if(link_overlay_selected.value.textContent.length > 1){
      return;
    }

    resetLinkOverlay()
  }

  const handleClickLinkOverlay = (event) => {
    if(!event || !event.target){
      return;
    }

    const target = event.target.closest('a');

    resetLinkOverlay()

    if (!target) {
      return;
    }

    if (target.hasAttribute('data-unmutable')) {
      return;
    }

    activateLinkOverlay(target);
  };

  const activateLinkOverlay = (target_link) => {
    setLinkOverlaySelected(target_link);
    setLinkOverlayVisible(true);


    const parent_position = editor_container_element.value.getBoundingClientRect();
    const position_link = target_link.getBoundingClientRect();

    setLinkOverlayStyle((position_link.left - parent_position.left) + 'px',
    (position_link.bottom - parent_position.top + 5) + 'px');
  }

  const onNavigateToLink = () => {
    window.open(link_overlay_selected.value.href, '_blank');
    setLinkOverlayVisible(false);
  };

  const onOpenLinkChangeModal = () => {
    editor.setSelection(link_overlay_selected.value);
    onLinksModalOpen();
    setLinkOverlayVisible(false);
  };

  const onRemoveLinkFromEditor = () => {
    editor.setSelection(link_overlay_selected.value)
    editor.removeLink();
    setLinkOverlayVisible(false);
  };

  const setLinkOverlaySelected = (element) => {
    link_overlay_selected.value = element;
  }

  const setLinkOverlayVisible = (value) => {
    link_overlay_visible.value = value;
  }

  const setLinkOverlayStyle = (position_left, position_top) => {
    link_overlay_style.value = {
      left: position_left,
      top: position_top
    };
  }

  const setImageOverlayVisible = (value) => {
    image_overlay_visible.value = value;
  }

  const resetLinkOverlay = () => {
    setLinkOverlayVisible(false);
    setLinkOverlaySelected(null);
  };

  const setEditorMenuVisibility = (value) => {
    editor_menu_visible.value = value;
  }

  defineExpose({
    editor : editor
  });

</script>

